import './polyfills'   // MUST be first — installs window.Buffer before web3.js loads

// ─── Stale-tab self-heal ───
// Code-splitting created a failure mode the single-bundle era never had: a
// tab open across a deploy asks for a lazy chunk whose hash no longer
// exists, the SPA fallback answers index.html at HTTP 200, and strict MIME
// checking kills the import ("Expected a JavaScript-or-Wasm module
// script..."). Vite fires `vite:preloadError` for exactly this; one reload
// swaps the tab onto the new build's index and every chunk resolves again.
// Rate-limited so a genuinely broken deploy can't reload-loop the browser.
window.addEventListener('vite:preloadError', (e) => {
  try {
    const last = +sessionStorage.getItem('cheddar_preload_reload') || 0
    if (Date.now() - last < 60_000) return          // already tried recently — let the error surface
    sessionStorage.setItem('cheddar_preload_reload', String(Date.now()))
    e.preventDefault()                              // suppress the throw; we're handling it
    window.location.reload()
  } catch {}
})

import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import SolCheddarWelcome from './SolCheddarWelcome'
import './styles.css'

// ─── Privy ─────────────────────────────────────────────────
// Privy no longer lives here. It used to wrap all of Root, which forced the
// entire SDK — and its transitive viem (a full EVM stack), @walletconnect and
// @coinbase/wallet-sdk — into the ENTRY bundle. The chart couldn't paint until
// all of that downloaded and parsed. The bundle visualizer showed Privy's
// node_modules dwarfing the whole of src/.
//
// It's now lazily mounted deep inside the app (components/PrivyRuntime.jsx,
// wired through DualWalletContext), so its chunk loads AFTER first paint. Same
// email login, same embedded wallet, same reconciler — it just arrives a beat
// after the chart instead of blocking it. The reconciler's signal (dropPrivy)
// is still raised here on the "D1 session gone" path and threaded down to the
// runtime through <App />.
const APP_PATH = '/main'
// Deep links live UNDER the app path (/main/<mint>) — every gate below must
// match subpaths, not just the bare path, or a deep link lands on Welcome.
const isAppPath = (p) => p === APP_PATH || p.startsWith(APP_PATH + '/')

// URL-driven entry so the app is bookmarkable at /main:
//   /main  → the App (guarded: middleware redirects logged-out visitors to
//            / before this even loads; this also re-checks /api/me so a
//            stale tab or a race can't show the shell without a session).
//   else   → the Welcome page. Entering pushes /main (history, so back
//            returns to welcome and the URL is shareable).
function Root() {
  const [path, setPath] = useState(window.location.pathname)
  // null = unknown (checking), true/false = resolved. Only gates the /main
  // direct-load case; entering via onEnter is already trusted (just logged in).
  const [authChecked, setAuthChecked] = useState(!isAppPath(window.location.pathname))
  const [authed, setAuthed] = useState(false)
  // Bumped by enterWelcome() to tell the reconciler to drop the bot wallet.
  const [dropPrivy, setDropPrivy] = useState(0)

  // Keep `path` in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Direct load of /main: confirm a session before rendering App. If absent,
  // bounce to welcome (the middleware also redirects server-side; this covers
  // the client-side race and keeps the URL honest).
  useEffect(() => {
    if (!isAppPath(path)) return
    if (authChecked) return
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled) return
        if (d && d.authed) { setAuthed(true); setAuthChecked(true) }
        else { enterWelcome() }
      })
      .catch(() => { if (!cancelled) enterWelcome() })
    return () => { cancelled = true }
  }, [path, authChecked])

  const enterApp = () => {
    // Called by Welcome's onEnter after a successful login — trusted.
    // If a deep link (/main/<mint>) bounced through login, replay it now so
    // the App boots straight onto that chart; otherwise plain /main.
    let target = APP_PATH
    try {
      const pending = sessionStorage.getItem('cheddar_pending_deeplink')
      if (pending) { target = APP_PATH + '/' + pending; sessionStorage.removeItem('cheddar_pending_deeplink') }
    } catch {}
    window.history.pushState({}, '', target)
    setAuthed(true)
    setAuthChecked(true)
    setPath(APP_PATH)
  }

  const enterWelcome = () => {
    // The D1 session is gone. Take the bot wallet with it — otherwise a live
    // Privy session outlives the terminal session, and whoever next gets
    // through the email gate inherits a connected wallet they never authorized
    // (and, with confirmation modals off, one that signs without asking).
    setDropPrivy(n => n + 1)
    // Landing here from an unauthenticated deep link? Remember the mint so
    // enterApp can replay it after login instead of silently dropping it.
    try {
      const m = window.location.pathname.match(/^\/main\/([1-9A-HJ-NP-Za-km-z]{32,44})\/?$/)
      if (m) sessionStorage.setItem('cheddar_pending_deeplink', m[1])
    } catch {}
    window.history.replaceState({}, '', '/')
    setAuthed(false)
    setAuthChecked(true)
    setPath('/')
  }

  if (isAppPath(path)) {
    if (!authChecked) return null            // brief: checking session
    if (authed) return <App />
    // fell through to not-authed — enterWelcome() already redirected; render
    // welcome on the next tick.
  }
  // The reconciler is NOT rendered here any more. It used to sit on the Welcome
  // branch so usePrivy() had a provider — but Privy wrapped everything then. Now
  // Privy only exists inside the app (post-login), the only place the wallet can
  // act, so the reconciler moved into PrivyRuntime and this branch is Privy-free.
  // dropPrivy is raised in enterWelcome() and consumed when the app next mounts.
  return <SolCheddarWelcome onEnter={enterApp} />
}

ReactDOM.createRoot(document.getElementById('root')).render(<Root />)